import { useTranslation } from "react-i18next";
import { useState, useRef } from "react";
import video1 from "@/assets/video1.png";
import video2 from "@/assets/video2.png";
import video3 from "@/assets/video3.png";
import video4 from "@/assets/video4.png";
import usa from "@/assets/countries/us.svg"
import japan from "@/assets/countries/jp.svg"
import uk from "@/assets/countries/United-Kingdom.svg"
import { Link, useNavigate } from "react-router-dom";
import { IoPlay } from "react-icons/io5";
import { motion } from "framer-motion";
import { landingMotionInView } from "@/utils/landingMotion";

// Swiper (mobile + iPad only)
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination } from "swiper/modules";

function VoiceCarouselMobile({
  voiceData,
  playingVideo,
  handlePlayVideo,
  handleVideoEnded,
  videoRefs,
  t,
}: {
  voiceData: any[];
  playingVideo: number | null;
  handlePlayVideo: (index: number) => void;
  handleVideoEnded: () => void;
  videoRefs: React.MutableRefObject<(HTMLVideoElement | null)[]>;
  t: (s: string) => string;
}) {
  return (
    <Swiper
      modules={[Pagination]}
      pagination={{ clickable: true }}
      spaceBetween={16}
      slidesPerView={1.25}
      breakpoints={{
        640: { slidesPerView: 1, spaceBetween: 18 },
        768: { slidesPerView: 2.2, spaceBetween: 20 }, // iPad
      }}
      className="pb-8 payout-swiper"
    >
      {voiceData.map((voice, index) => (
        <SwiperSlide key={index} className="h-auto py-14 ">
          <div className="mx-auto w-full md:w-fit">
            <div className="md:max-w-[292px] h-[350px]  relative rounded-[18px] border border-white/80 bg-gradient-to-b from-white/10 to-white/10 p-2.5  ">
              <div className="relative h-full w-full overflow-hidden rounded-[18px] bg-black/40">
                <video
                  ref={(el) => {
                    videoRefs.current[index] = el;
                  }}
                  src={voice.videoSrc}
                  className="h-full w-full object-cover"
                  poster={voice.image}
                  onEnded={handleVideoEnded}
                  muted
                />

                {playingVideo !== index && (
                  <>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <button
                        onClick={() => handlePlayVideo(index)}
                        className="h-[61px] w-[61px] rounded-full bg-gradient-to-r from-[#23d8e6] to-[#D6E242] flex items-center justify-center"
                        aria-label="Play video"
                      >
                        <IoPlay size={24} className="text-white" />
                      </button>
                    </div>

                    <div className="absolute inset-x-0 bottom-0 px-4 pb-4 pt-10">
                      <div className="flex items-end justify-between gap-3">
                        <div>
                          <h2 className="text-white text-base md:text-lg font-normal italic">
                            {t(voice.name)}
                          </h2>
                          <p className="text-white/50 text-sm md:text-base font-medium font-creato">
                            {voice.amount}
                          </p>
                        </div>
                        <img
                          src={voice.flag}
                          alt="flag logo"
                          className="w-[32px] h-[32px] md:w-[42px] md:h-[42px] rounded-full"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
}

export default function VoiceSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [playingVideo, setPlayingVideo] = useState<number | null>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  const voiceData = [
    {
      indexNo: "3",
      name: "Stacy M.",
      amount: "$10,282.61",
      country: "United States",
      flag: usa,
      image: video1,
      videoSrc: "https://player.vimeo.com/video/1109095641?h=2400709fb6&autoplay=0&title=0&byline=0&portrait=0", // Add video URL if needed
    },
    {
      indexNo: "7",
      name: "Haruki A.",
      amount: "$5,238.60",
      country: "Japan",
      flag: japan,
      image: video2,
      videoSrc: "https://player.vimeo.com/video/1109095724?h=081881d143&autoplay=0&title=0&byline=0&portrait=0",
    },
    {
      indexNo: "9",
      name: "Mike B.",
      amount: "$2,455.22",
      country: "United Kingdom",
      flag: uk,
      image: video3,
      videoSrc: "https://player.vimeo.com/video/1109095897?h=12ec99c937&autoplay=0&title=0&byline=0&portrait=0",
    },
    {
      indexNo: "1",
      name: "Jason W.",
      amount: "$7,892.15",
      country: "United States",
      flag: usa,
      image: video4,
      videoSrc: "https://player.vimeo.com/video/1109095683?h=71c85a0269&autoplay=0&title=0&byline=0&portrait=0",
    },
  ];

  const handlePlayVideo = (index: number) => {
    navigate(`/testimonials?page=${voiceData[index].indexNo}`);
  };

  const handleVideoEnded = () => {
    setPlayingVideo(null);
  };

  return (
    <>
      <div className="relative w-full h-auto  pt-20 px-0 flex flex-col items-center justify-center  ">

        <div className="mx-auto px-4 xl:px-0 max-w-[1020px] text-center">
          <motion.h2
            className="font-nexa text-[32px] font-normal leading-tight tracking-tight md:text-[56px]"
            {...landingMotionInView}
          >
            {t("Voices of the")}
            <span className="bg-gradient-to-r px-2 from-[#D6E242] via-[#9DDF78] to-[#79DDC7] bg-clip-text italic text-transparent">
              {t("URFX Community")}
            </span>
          </motion.h2>
          <motion.p
            className="mx-auto font-normal max-w-[590px] pt-1 font-nexa text-lg leading-tight text-white/50 md:text-xl"
            {...landingMotionInView}
          >
            {t(
              "Explore real experiences from traders worldwide—organized by language, region, and trading style."
            )}
          </motion.p>
        </div>
      </div>
      <div className="xl:w-[88%] pl-4 xl:pl-0 mx-auto z-50 font-nexa pb-10 md:pb-20 md:pt-12">
        {/* Mobile + iPad swiper */}
        <motion.div className="block lg:hidden relative z-20 overflow-hidden" {...landingMotionInView}>
          <VoiceCarouselMobile
            voiceData={voiceData}
            playingVideo={playingVideo}
            handlePlayVideo={handlePlayVideo}
            handleVideoEnded={handleVideoEnded}
            videoRefs={videoRefs}
            t={t}
          />
        </motion.div>

        {/* Desktop (keep original staggered layout) */}
        <motion.div
          className="hidden lg:flex w-full relative z-20 items-start xl:justify-center gap-6 overflow-x-auto hide-scrollbar pb-4"
          {...landingMotionInView}
        >
          {voiceData.map((voice, index) => (
            <div
              key={index}
              className={`flex-shrink-0 md:max-w-[292px] md:min-h-[352px] max-w-[230px] min-h-[250px] relative rounded-[18px] border border-white/80 bg-gradient-to-b from-white/10 to-white/10 p-2.5  ${index % 2 === 0 ? "mt-14 md:mt-20" : "mt-0"}`}
            >
              <div className="relative h-full w-full overflow-hidden rounded-[18px]  bg-black/40">
                <video
                  ref={(el) => {
                    videoRefs.current[index] = el;
                  }}
                  src={voice.videoSrc}
                  className="h-full w-full object-cover"
                  poster={voice.image}
                  onEnded={handleVideoEnded}
                  muted
                />

                {/* Content overlay - hidden when video is playing */}
                {playingVideo !== index && (
                  <>
                    {/* center play */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <button
                        onClick={() => handlePlayVideo(index)}
                        className="h-[61px] w-[61px] rounded-full bg-gradient-to-r from-[#23d8e6] to-[#D6E242]     flex items-center justify-center "
                        aria-label="Play video"
                      >
                        <IoPlay size={24} className="text-white" />
                      </button>
                    </div>

                    {/* bottom content */}
                    <div className="absolute inset-x-0 bottom-0 px-4 pb-4 pt-10">
                      <div className="flex items-end justify-between gap-3">
                        <div>
                          <h2 className="text-white text-base md:text-lg font-normal italic ">
                            {t(voice.name)}
                          </h2>
                          <p className="text-white/50 text-sm md:text-base font-medium font-creato">
                            {voice.amount}
                          </p>
                        </div>
                        <img
                          src={voice.flag}
                          alt="flag logo"
                          className="w-[32px] h-[32px] md:w-[42px] md:h-[42px] rounded-full"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </motion.div>

        <motion.p className="mt-4 max-w-2xl mx-auto text-center text-white/50 font-neue text-lg !font-normal tracking-normal">
          Some content may feature paid actors and may be dramatized. Figures are individual results—not typical or guaranteed—and subject to our Terms & Conditions and Trading Rules. Trading involves risk.
        </motion.p>
        <motion.div
          className="flex justify-center mt-10"
          {...landingMotionInView}
        >
          <Link to="/testimonials" 
           className="btn-gradient-left w-fit min-w-[160px] rounded-[10px] px-6 py-2.5 text-lg font-normal">
            
              {t("View All Success stories")}
          </Link>
        </motion.div>
      </div>
    </>
  );
}

